import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bell, CheckCircle2, MessageSquare, Camera, MapPin, Clock, BarChart3, Users } from "lucide-react";

export const ProductShowcase = () => {
  return (
    <section id="product" data-testid="product-showcase-section" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-emerald-700">
            One product, three experiences
          </div>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-medium text-stone-900 tracking-tight">
            Parents, staff & owners — each in their lane.
          </h2>
        </div>

        <Tabs defaultValue="parent" className="mt-12">
          <TabsList
            data-testid="product-tabs-list"
            className="bg-stone-100 rounded-xl p-1 h-auto flex-wrap gap-1"
          >
            <TabsTrigger
              value="parent"
              data-testid="tab-parent"
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-5 py-2.5 font-medium"
            >
              Parent App
            </TabsTrigger>
            <TabsTrigger
              value="staff"
              data-testid="tab-staff"
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-5 py-2.5 font-medium"
            >
              Staff App
            </TabsTrigger>
            <TabsTrigger
              value="admin"
              data-testid="tab-admin"
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-5 py-2.5 font-medium"
            >
              Admin Dashboard
            </TabsTrigger>
          </TabsList>

          <TabsContent value="parent" className="mt-10">
            <ParentMockup />
          </TabsContent>
          <TabsContent value="staff" className="mt-10">
            <StaffMockup />
          </TabsContent>
          <TabsContent value="admin" className="mt-10">
            <AdminMockup />
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

const PhoneFrame = ({ children, testid }) => (
  <div data-testid={testid} className="mx-auto w-[300px] h-[600px] bg-stone-900 rounded-[3rem] p-3 shadow-lift">
    <div className="w-full h-full bg-stone-50 rounded-[2.3rem] overflow-hidden relative">
      <div className="absolute top-0 inset-x-0 h-7 bg-stone-900 rounded-b-2xl w-32 mx-auto z-10" />
      {children}
    </div>
  </div>
);

const ParentMockup = () => (
  <div className="grid lg:grid-cols-2 gap-12 items-center">
    <PhoneFrame testid="parent-phone-mockup">
      <div className="pt-10 px-5 pb-5 h-full overflow-hidden">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] text-stone-500 uppercase tracking-widest">Good morning</div>
            <div className="font-display text-lg font-semibold text-stone-900">Mia's day</div>
          </div>
          <div className="w-9 h-9 rounded-full bg-orange-200 grid place-items-center text-orange-800 text-xs font-bold">M</div>
        </div>
        <div className="mt-5 rounded-2xl bg-emerald-700 text-white p-4">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-emerald-200">Now · 10:42</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-300" />
          </div>
          <div className="mt-2 font-display text-base leading-tight">Mia started circle time with Ms. Danielle.</div>
        </div>
        <div className="mt-4 space-y-3">
          <UpdateRow Icon={Camera} color="bg-orange-500/10 text-orange-600" title="New photo added" time="09:18" />
          <UpdateRow Icon={CheckCircle2} color="bg-emerald-700/10 text-emerald-700" title="Nap 1h 22m" time="13:04" />
          <UpdateRow Icon={MessageSquare} color="bg-stone-200 text-stone-700" title="Teacher note · nutrition" time="12:10" />
        </div>
        <div className="mt-5 rounded-xl border border-stone-200 bg-white p-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Tuition · Mar</div>
            <div className="font-display text-base font-semibold text-stone-900">$820.00</div>
          </div>
          <button className="text-xs font-semibold text-emerald-700">Pay now</button>
        </div>
      </div>
    </PhoneFrame>
    <div>
      <h3 className="font-display text-3xl font-medium text-stone-900 tracking-tight">
        A calm feed. One tap to pay. Zero guesswork.
      </h3>
      <p className="mt-4 text-stone-600 max-w-lg">
        Parents get the updates that matter — photos, nap times, meals, messages — without the chatter. They
        also sign enrollment, pay tuition, and reply to teachers from one screen.
      </p>
      <ul className="mt-6 space-y-3">
        {["Push notifications you can actually trust", "Secure messaging with teachers", "ACH & card auto-pay via Stripe", "E-sign DCY 01234 & annual updates"].map(
          (t, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-stone-700">
              <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
              {t}
            </li>
          )
        )}
      </ul>
    </div>
  </div>
);

const UpdateRow = ({ Icon, color, title, time }) => (
  <div className="flex items-center gap-3 rounded-xl bg-white border border-stone-200 p-3">
    <div className={`w-9 h-9 rounded-lg grid place-items-center ${color}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="flex-1">
      <div className="text-xs font-semibold text-stone-800">{title}</div>
      <div className="text-[10px] text-stone-500">{time}</div>
    </div>
  </div>
);

const StaffMockup = () => (
  <div className="grid lg:grid-cols-2 gap-12 items-center">
    <div className="order-2 lg:order-1">
      <h3 className="font-display text-3xl font-medium text-stone-900 tracking-tight">
        GPS clock-in. Ratios in your pocket.
      </h3>
      <p className="mt-4 text-stone-600 max-w-lg">
        Teachers clock in only when inside the geo-fence. They see current room ratios and group caps at a
        glance. If a ratio is about to break, they get a warning before admin ever sees it.
      </p>
      <ul className="mt-6 space-y-3">
        {["Geo-fenced clock in/out", "Live ratio widget per room", "Digital daily sign-in (CCU-204)", "Incident/injury form (DCY 01299) on mobile"].map(
          (t, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-stone-700">
              <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
              {t}
            </li>
          )
        )}
      </ul>
    </div>
    <div className="order-1 lg:order-2">
      <PhoneFrame testid="staff-phone-mockup">
        <div className="pt-10 px-5 pb-5 h-full">
          <div className="text-[11px] uppercase tracking-widest text-stone-500">Hi, Danielle</div>
          <div className="font-display text-xl font-semibold text-stone-900">Toddler Room A</div>

          <div className="mt-5 rounded-2xl bg-stone-900 text-stone-50 p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-300">
              <MapPin className="w-3 h-3" /> On-site · verified
            </div>
            <div className="mt-3 font-display text-3xl font-medium">07:58 am</div>
            <button className="mt-4 w-full bg-emerald-600 hover:bg-emerald-500 rounded-xl py-3 font-semibold text-sm flex items-center justify-center gap-2">
              <Clock className="w-4 h-4" /> Clock in
            </button>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-stone-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Ratio</div>
              <div className="font-display text-xl font-semibold text-emerald-700">1:7</div>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Present</div>
              <div className="font-display text-xl font-semibold text-stone-900">14/14</div>
            </div>
          </div>
        </div>
      </PhoneFrame>
    </div>
  </div>
);

const AdminMockup = () => (
  <div className="rounded-3xl border border-stone-200 bg-stone-50 p-3 shadow-lift overflow-hidden">
    <div className="flex items-center gap-1.5 px-3 py-2">
      <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
      <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
      <div className="ml-3 text-[11px] text-stone-500">app.childcheck.ohio / dashboard</div>
    </div>
    <div className="bg-white rounded-2xl p-6 sm:p-8 grid lg:grid-cols-12 gap-6" data-testid="admin-mockup">
      <div className="lg:col-span-8 space-y-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500">Tuesday · Columbus center</div>
          <div className="font-display text-2xl font-semibold text-stone-900 mt-1">Center overview</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { l: "Present", v: "47/52", Icon: Users, tone: "emerald" },
            { l: "Ratios ok", v: "6/6", Icon: CheckCircle2, tone: "emerald" },
            { l: "Revenue Mar", v: "$48.2k", Icon: BarChart3, tone: "orange" },
            { l: "Staff on", v: "9", Icon: Clock, tone: "stone" },
          ].map((k, i) => (
            <div key={i} className="rounded-xl border border-stone-200 bg-stone-50 p-4">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">{k.l}</div>
                <k.Icon
                  className={`w-4 h-4 ${k.tone === "emerald" ? "text-emerald-700" : k.tone === "orange" ? "text-orange-600" : "text-stone-700"}`}
                />
              </div>
              <div className="mt-2 font-display text-xl font-semibold text-stone-900">{k.v}</div>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-stone-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-base font-medium text-stone-900">Attendance · last 7 days</div>
            <div className="text-[10px] text-emerald-700 uppercase tracking-widest font-semibold">+4.2%</div>
          </div>
          <div className="flex items-end gap-2 h-28">
            {[60, 72, 68, 84, 90, 78, 94].map((h, i) => (
              <div key={i} className="flex-1 rounded-t-md bg-gradient-to-t from-emerald-800 to-emerald-600" style={{ height: `${h}%` }} />
            ))}
          </div>
        </div>
      </div>

      <div className="lg:col-span-4 space-y-4">
        <div className="rounded-xl border border-stone-200 p-4">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Compliance</div>
            <Bell className="w-3.5 h-3.5 text-orange-600" />
          </div>
          <div className="mt-3 font-display text-xl font-semibold text-stone-900">94% audit-ready</div>
          <div className="mt-2 text-xs text-stone-500">2 DCY 01234 updates due this week</div>
          <div className="mt-3 h-1.5 rounded-full bg-stone-100 overflow-hidden">
            <div className="h-full w-[94%] bg-emerald-700 rounded-full"></div>
          </div>
        </div>
        <div className="rounded-xl bg-stone-900 text-stone-50 p-4">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300">Live ratio</div>
          <div className="mt-2 space-y-2">
            {[
              { room: "Infants", r: "1:5", ok: true },
              { room: "Toddler A", r: "1:7", ok: true },
              { room: "Preschool", r: "1:12", ok: true },
              { room: "Pre-K", r: "1:14", ok: true },
            ].map((row) => (
              <div key={row.room} className="flex items-center justify-between text-sm">
                <span className="text-stone-300">{row.room}</span>
                <span className="font-display font-semibold">{row.r}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default ProductShowcase;
